import React, { useState } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Menu as MenuIcon,
  MenuOpen,
  Logout,
  AccountCircle,
} from "@mui/icons-material";
import AdminSidebar, { drawerWidth, miniDrawerWidth } from "./AdminSidebar";
import ForcePasswordChangeDialog from "./common/ForcePasswordChangeDialog";

// Shared shell for all authenticated admin pages: a responsive, collapsible
// sidebar with an app bar and content area whose widths always stay in sync.
function AdminLayout({ title, subtitle, onLogout, actions, children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(true); // desktop: expanded vs mini
  const [mobileOpen, setMobileOpen] = useState(false); // mobile: overlay visibility
  const [anchorEl, setAnchorEl] = useState(null);

  const admin = JSON.parse(localStorage.getItem("adminUser") || "null");
  const initials = (admin?.name || admin?.email || "A").trim().charAt(0).toUpperCase();
  const [forcePwOpen, setForcePwOpen] = useState(
    () => localStorage.getItem("requirePasswordChange") === "true"
  );

  const currentWidth = isMobile ? 0 : open ? drawerWidth : miniDrawerWidth;
  const toggle = () => setOpen((v) => !v);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        sx={{
          width: { xs: "100%", md: `calc(100% - ${currentWidth}px)` },
          ml: { xs: 0, md: `${currentWidth}px` },
          transition: "width 0.25s ease, margin 0.25s ease",
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={isMobile ? () => setMobileOpen(true) : toggle}
            aria-label="toggle navigation"
          >
            {isMobile ? <MenuIcon /> : open ? <MenuOpen /> : <MenuIcon />}
          </IconButton>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {actions}
          {onLogout && (
            <>
              <Tooltip title="Account">
                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ ml: 1 }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      fontSize: 15,
                      fontWeight: 700,
                      background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
                    }}
                  >
                    {initials}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{ elevation: 3, sx: { mt: 1, minWidth: 220 } }}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="subtitle2" noWrap>
                    {admin?.name || "Administrator"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                    {admin?.email || ""}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem disabled>
                  <ListItemIcon>
                    <AccountCircle fontSize="small" />
                  </ListItemIcon>
                  {admin?.role || "Admin"}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    onLogout();
                  }}
                >
                  <ListItemIcon>
                    <Logout fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      <AdminSidebar
        open={isMobile ? mobileOpen : open}
        isMobile={isMobile}
        onClose={() => setMobileOpen(false)}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: { xs: "100%", md: `calc(100% - ${currentWidth}px)` },
          transition: "width 0.25s ease",
          minWidth: 0,
        }}
      >
        <Toolbar />
        {children}
      </Box>
      <ForcePasswordChangeDialog
        open={forcePwOpen && Boolean(admin?.id)}
        userId={admin?.id}
        onComplete={() => setForcePwOpen(false)}
      />
    </Box>
  );
}

export default AdminLayout;
